import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../state/auth.js';
import { translateError } from '../i18n.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { openFile } from '../utils/openFile.js';

// Gebruikersoverleg (sportcomplex fase 2b), in a club housed at a complex: the bestuur of
// the club is a member of the complex's meeting bodies. Here they read the meetings and
// decisions, propose agenda items and say whether they come. The complex holds the
// meetings; nothing of the meeting itself is changed from a club.
// /complex/overleg lists them, /complex/overleg/:id opens one.

const ERRORS = {
  title_required: 'Geef het agendapunt een titel.', proposals_closed: 'Agendapunten aandragen kan niet meer: de vergadering is begonnen.',
  proposal_decided: 'Dit voorstel is al behandeld.', meeting_closed: 'Aan- of afmelden kan niet meer voor deze vergadering.', not_found: 'Deze vergadering bestaat niet of je mag hem niet zien.',
};
const message = err => ERRORS[err.code] || translateError(err.code) || 'Dat lukte niet.';
// Wall-clock times of the complex ("YYYY-MM-DDTHH:mm"): shown as they are, no time zone
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const when = v => (v ? `${Number(v.slice(8, 10))} ${MONTHS[Number(v.slice(5, 7)) - 1]} ${v.slice(0, 4)}, ${v.slice(11, 16)}` : '');
const STATUS = { gepland: 'Gepland', gehouden: 'Gehouden', geannuleerd: 'Geannuleerd' };
const RSVP = { aangemeld: 'Je komt', afgemeld: 'Je bent afgemeld' };
const PROPOSAL = { voorgesteld: 'Voorgesteld', geaccepteerd: 'Op de agenda', afgewezen: 'Afgewezen' };
const muted = 'font-size:12px;color:var(--color-text-muted)';

export default function ComplexOverlegView({ id }) {
  return id ? <OverlegMeeting key={id} id={id} /> : <OverlegList />;
}

function OverlegList() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { api('/api/complexes/shared/meetings').then(setData).catch(err => setError(message(err))); }, []);
  if (error) return <div class="alert alert-error">{error}</div>;
  if (!data) return <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>;
  if (!data.complex) return <div class="alert alert-error">Je bent geen vertegenwoordiger van je club bij een sportcomplex.</div>;
  return (
    <div>
      <div class="page-header" style="margin-bottom:16px">
        <div>
          <h1 class="page-title">Gebruikersoverleg</h1>
          <div style={muted}>🏟 {data.complex.name} · je bent erbij als lid van het bestuur van je club</div>
        </div>
      </div>
      {data.bodies.length === 0 && <Card><div class="text-sm text-muted">Het complex heeft nog geen overleg gepland.</div></Card>}
      {data.bodies.map(b => (
        <Card key={b.id} title={b.name} style="margin-bottom:16px">
          {b.description && <div style={`${muted};margin-bottom:8px`}>{b.description}</div>}
          {b.meetings.length === 0 && <div class="text-sm text-muted">Nog geen vergaderingen.</div>}
          {b.meetings.map(m => (
            <a key={m.id} href={`/complex/overleg/${m.id}`} onClick={e => { e.preventDefault(); route(`/complex/overleg/${m.id}`); }}
              style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:8px 0;border-top:1px solid var(--color-border);color:inherit;text-decoration:none">
              <span style="flex:1 1 200px;min-width:0"><strong>{m.title}</strong><span style={`${muted};display:block`}>{when(m.starts_at)}{m.location ? ` · ${m.location}` : ''}</span></span>
              <span style={muted}>{[STATUS[m.status], m.status === 'gepland' && RSVP[m.my_rsvp], m.minutes_status === 'vastgesteld' && 'notulen vastgesteld'].filter(Boolean).join(' · ')}</span>
              <span aria-hidden="true" class="text-muted">›</span>
            </a>
          ))}
        </Card>
      ))}
      {data.decisions.length > 0 && (
        <Card title="Besluiten">
          {data.decisions.map(d => (
            <div key={d.id} style="padding:6px 0;border-top:1px solid var(--color-border)">
              <div style="font-size:14px"><strong>{d.number}</strong> {d.text}</div>
              <div style={muted}>{d.body_name} · {d.meeting_title} · {when(d.meeting_starts_at).split(',')[0]}</div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function OverlegMeeting({ id }) {
  const [m, setM] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: '', description: '' });
  const load = () => api(`/api/complexes/shared/meetings/${id}`).then(d => { setM(d); setError(null); }).catch(err => setError(message(err)));
  useEffect(() => { load(); }, [id]);
  async function run(fn) {
    setBusy(true); setError(null);
    try { await fn(); await load(); return true; } catch (err) { setError(message(err)); return false; } finally { setBusy(false); }
  }
  const back = <a href="/complex/overleg" onClick={e => { e.preventDefault(); route('/complex/overleg'); }} style="font-size:13px;color:var(--color-text-muted);text-decoration:none">‹ Gebruikersoverleg</a>;
  if (!m) return <div>{back}{error ? <div class="alert alert-error" style="margin-top:12px">{error}</div> : <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>}</div>;
  const rsvp = status => run(() => api(`/api/complexes/shared/meetings/${id}/rsvp`, { method: 'PUT', body: JSON.stringify({ status }) }));
  const propose = () => run(async () => {
    await api(`/api/complexes/shared/meetings/${id}/proposals`, { method: 'POST', body: JSON.stringify(form) });
    setForm({ title: '', description: '' });
  });
  const withdraw = p => confirm(`"${p.title}" intrekken?`) && run(() => api(`/api/complexes/shared/meetings/${id}/proposals/${p.id}`, { method: 'DELETE' }));
  return (
    <div>
      {back}
      <div class="page-header" style="margin-top:6px;margin-bottom:12px">
        <div>
          <h1 class="page-title">{m.title}</h1>
          <div style={muted}>🏟 {m.complex.name} · {m.body.name} · {when(m.starts_at)}{m.location ? ` · ${m.location}` : ''} · {STATUS[m.status]}</div>
        </div>
      </div>
      {error && <div class="alert alert-error mb-4">{error}</div>}

      {m.can_rsvp && (
        <Card title="Kom je?" style="margin-bottom:16px">
          <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
            <Button size="sm" variant={m.my_rsvp === 'aangemeld' ? 'primary' : 'secondary'} disabled={busy} onClick={() => rsvp(m.my_rsvp === 'aangemeld' ? null : 'aangemeld')}>Ik kom</Button>
            <Button size="sm" variant={m.my_rsvp === 'afgemeld' ? 'primary' : 'secondary'} disabled={busy} onClick={() => rsvp(m.my_rsvp === 'afgemeld' ? null : 'afgemeld')}>Ik meld me af</Button>
            <span style={muted}>{RSVP[m.my_rsvp] || 'Nog niet doorgegeven'} · de secretaris van het complex ziet het</span>
          </div>
        </Card>
      )}

      {m.minutes_document_id && (
        <Card style="margin-bottom:16px">
          <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
            <span style="flex:1">De notulen zijn vastgesteld.</span>
            <Button size="sm" variant="secondary" onClick={() => openFile(`/api/complexes/shared/documents/${m.minutes_document_id}/preview`, `Notulen ${m.title}.pdf`)}>📄 Notulen (PDF)</Button>
          </div>
        </Card>
      )}

      <Card title="Agenda" style="margin-bottom:16px">
        {m.agenda.map((a, i) => (
          <div key={a.id} style="padding:8px 0;border-top:1px solid var(--color-border)">
            <div style="font-size:14px;font-weight:600">{i + 1}. {a.title}</div>
            {a.description && <div style="font-size:13px;color:var(--color-text-muted);white-space:pre-wrap">{a.description}</div>}
            {a.minutes && <div style="font-size:13px;white-space:pre-wrap;margin-top:4px">{a.minutes}</div>}
          </div>
        ))}
        {m.decisions.length > 0 && (
          <div style="margin-top:10px">
            <div style="font-weight:600;font-size:14px;margin-bottom:4px">Besluiten</div>
            {m.decisions.map(d => <div key={d.id} style="font-size:13px;padding:2px 0"><strong>{d.number}</strong> {d.text}</div>)}
          </div>
        )}
      </Card>

      {(m.can_propose || m.proposals.length > 0) && (
        <Card title="Agendapunt aandragen">
          {m.proposals.map(p => (
            <div key={p.id} style="display:flex;gap:8px;align-items:flex-start;padding:6px 0;border-bottom:1px solid var(--color-border)">
              <div style="flex:1;min-width:0">
                <div style="font-size:14px;font-weight:600">{p.title}</div>
                {p.description && <div style={`${muted};white-space:pre-wrap`}>{p.description}</div>}
                <div style={muted}>{PROPOSAL[p.status]}{p.status === 'afgewezen' && p.reason ? ` — ${p.reason}` : ''}</div>
              </div>
              {p.status === 'voorgesteld' && <Button size="sm" variant="secondary" disabled={busy} onClick={() => withdraw(p)}>Intrekken</Button>}
            </div>
          ))}
          {m.can_propose && (
            <div style="display:flex;flex-direction:column;gap:8px;margin-top:8px">
              <input class="form-input" maxLength={200} value={form.title} onInput={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Titel, bijvoorbeeld: Verlichting veld 3" aria-label="Titel van het agendapunt" />
              <textarea class="form-input" rows={2} maxLength={2000} value={form.description} onInput={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Toelichting (optioneel)" aria-label="Toelichting" />
              <div><Button size="sm" disabled={busy || !form.title.trim()} onClick={propose}>Aandragen</Button> <span style={muted}>De voorzitter of secretaris van het complex zet het op de agenda.</span></div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
